import { useEffect, useMemo, useRef } from "react";
import {
  type CandlestickData,
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  createChart,
  createSeriesMarkers,
  HistogramSeries,
  type IChartApi,
  LineStyle,
  type MouseEventParams,
  type SeriesMarker,
  type Time,
  type UTCTimestamp,
  type WhitespaceData,
} from "lightweight-charts";

import type { ChartBarView, ChartFillView, ChartMarkup } from "./chart-models.js";
import { buildMarketTimeline, chartTimeForInstant } from "./market-timeline.js";
import { ChartAnnotationsPrimitive } from "./chart-annotations-primitive.js";

const EMPTY_FILLS: readonly ChartFillView[] = [];
const EMPTY_ANNOTATIONS: readonly ChartMarkup[] = [];
const EMPTY_DATES: readonly string[] = [];

function time(value: number): UTCTimestamp {
  return Math.floor(value) as UTCTimestamp;
}

function isBuy(action: string): boolean {
  return action === "enter-long" || action === "exit-short" || action === "reduce-short";
}

export function MarketChart({
  bars,
  fills = EMPTY_FILLS,
  focusAt,
  selectedFillId,
  timeframe = "intraday",
  annotations = EMPTY_ANNOTATIONS,
  selectedAnnotationId,
  onAnnotationSelect,
  onBarSelect,
  evidenceSessionDates = EMPTY_DATES,
}: {
  readonly bars: readonly ChartBarView[];
  readonly fills?: readonly ChartFillView[];
  readonly focusAt?: string;
  readonly selectedFillId?: string;
  readonly timeframe?: "intraday" | "daily";
  readonly annotations?: readonly ChartMarkup[];
  readonly selectedAnnotationId?: string;
  readonly onAnnotationSelect?: (id: string) => void;
  readonly onBarSelect?: (bar: ChartBarView) => void;
  readonly evidenceSessionDates?: readonly string[];
}) {
  const container = useRef<HTMLDivElement>(null);
  const chartApi = useRef<IChartApi | null>(null);
  const logicalIndices = useRef<ReadonlyMap<number, number>>(new Map());
  const timeline = useMemo(() => buildMarketTimeline(bars, timeframe), [bars, timeframe]);
  const annotationPrimitive = useRef<ChartAnnotationsPrimitive | null>(null);

  useEffect(() => {
    const element = container.current;
    if (element === null || timeline.bars.length === 0) return;
    const chart = createChart(element, {
      autoSize: true,
      layout: {
        attributionLogo: false,
        background: { color: "#071610", type: ColorType.Solid },
        textColor: "#9fb9ad",
      },
      grid: {
        horzLines: { color: "rgba(61, 105, 86, 0.22)" },
        vertLines: { color: "rgba(61, 105, 86, 0.22)" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        horzLine: {
          color: "rgba(159, 185, 173, 0.72)",
          labelBackgroundColor: "#1d2b25",
          labelVisible: true,
          style: LineStyle.Dashed,
          width: 1,
        },
        vertLine: {
          color: "rgba(159, 185, 173, 0.72)",
          labelBackgroundColor: "#1d2b25",
          labelVisible: true,
          style: LineStyle.Dashed,
          width: 1,
        },
      },
      localization: {
        timeFormatter: (chartTime: Time) =>
          typeof chartTime === "number"
            ? (timeline.labels.get(chartTime)?.full ?? "Eastern time")
            : "Eastern time",
      },
      rightPriceScale: { borderColor: "rgba(92, 145, 120, 0.45)" },
      timeScale: {
        borderColor: "rgba(92, 145, 120, 0.45)",
        secondsVisible: false,
        tickMarkFormatter: (chartTime: Time) =>
          typeof chartTime === "number" ? (timeline.labels.get(chartTime)?.axis ?? null) : null,
        timeVisible: timeframe !== "daily",
      },
    });
    chartApi.current = chart;
    const candles = chart.addSeries(CandlestickSeries, {
      borderVisible: false,
      downColor: "#ff806f",
      upColor: "#6ee7ad",
      wickDownColor: "#ff806f",
      wickUpColor: "#6ee7ad",
    });
    candles.priceScale().applyOptions({ scaleMargins: { bottom: 0.22, top: 0.05 } });
    const candleData = new Map<
      number,
      CandlestickData<UTCTimestamp> | WhitespaceData<UTCTimestamp>
    >();
    timeline.sessions.forEach((session) => {
      candleData.set(session.openTime, { time: time(session.openTime) });
      candleData.set(session.closeTime, { time: time(session.closeTime) });
    });
    timeline.bars.forEach(({ bar, chartTime }) => {
      candleData.set(chartTime, {
        close: bar.close,
        high: bar.high,
        low: bar.low,
        open: bar.open,
        time: time(chartTime),
      });
    });
    const orderedCandles = [...candleData.values()].sort(
      (left, right) => Number(left.time) - Number(right.time),
    );
    candles.setData(orderedCandles);
    const primitive = new ChartAnnotationsPrimitive(timeline);
    candles.attachPrimitive(primitive);
    annotationPrimitive.current = primitive;
    // Session-boundary whitespace also occupies logical positions. Include it
    // when focusing executions so long histories do not drift by many sessions.
    logicalIndices.current = new Map(
      orderedCandles.map((point, index) => [Number(point.time), index]),
    );
    const markers: SeriesMarker<UTCTimestamp>[] = [...fills]
      .sort((left, right) => left.occurredAt.localeCompare(right.occurredAt))
      .flatMap((fill) => {
        const fillTime = chartTimeForInstant(timeline, fill.occurredAt);
        if (fillTime === undefined) return [];
        const buy = isBuy(fill.action);
        const selected = fill.id === selectedFillId;
        return [
          {
            color: selected ? "#ffcf66" : buy ? "#6ee7ad" : "#ff806f",
            position: buy ? "belowBar" : "aboveBar",
            shape: buy ? "arrowUp" : "arrowDown",
            text: `${fill.action} ${fill.quantity} @ $${fill.priceUsd}`,
            time: time(fillTime),
          },
        ];
      });
    const cited = new Set(evidenceSessionDates);
    const evidenceMarkers: SeriesMarker<UTCTimestamp>[] = timeline.bars
      .filter(({ bar }) => cited.has(bar.sessionDate))
      .map(({ chartTime }) => ({
        time: time(chartTime),
        position: "aboveBar",
        shape: "circle",
        color: "#ffcf66",
        text: "Evidence",
        size: 0.5,
      }));
    createSeriesMarkers(
      candles,
      [...markers, ...evidenceMarkers].sort((a, b) => Number(a.time) - Number(b.time)),
    );
    const volume = chart.addSeries(HistogramSeries, {
      lastValueVisible: false,
      priceFormat: { type: "volume" },
      priceLineVisible: false,
      priceScaleId: "",
    });
    volume.priceScale().applyOptions({ scaleMargins: { bottom: 0, top: 0.8 } });
    volume.setData(
      timeline.bars.map(({ bar, chartTime }) => ({
        color: bar.close >= bar.open ? "rgba(110, 231, 173, 0.38)" : "rgba(255, 128, 111, 0.38)",
        time: time(chartTime),
        value: bar.volume,
      })),
    );
    const crosshairTooltip = document.createElement("div");
    crosshairTooltip.className = "market-chart-crosshair-tooltip";
    crosshairTooltip.hidden = true;
    element.append(crosshairTooltip);
    const chartBarByTime = new Map(
      timeline.bars.map(({ bar, chartTime }) => [chartTime, bar] as const),
    );
    const handleClick = (param: MouseEventParams) => {
      const annotationId = param.hoveredInfo?.objectId ?? param.hoveredObjectId;
      if (typeof annotationId === "string" && annotationId.startsWith("annotation:")) {
        onAnnotationSelect?.(annotationId.slice("annotation:".length));
        return;
      }
      const bar = typeof param.time === "number" ? chartBarByTime.get(param.time) : undefined;
      if (bar !== undefined) onBarSelect?.(bar);
    };
    chart.subscribeClick(handleClick);
    const updateCrosshairTooltip = (param: MouseEventParams) => {
      if (param.time === undefined || typeof param.time !== "number") {
        crosshairTooltip.hidden = true;
        return;
      }
      const chartBar = chartBarByTime.get(param.time);
      if (chartBar === undefined) {
        crosshairTooltip.hidden = true;
        return;
      }
      crosshairTooltip.textContent = `O: ${chartBar.open.toFixed(2)}  H: ${chartBar.high.toFixed(2)}  L: ${chartBar.low.toFixed(2)}  C: ${chartBar.close.toFixed(2)}  V: ${chartBar.volume.toLocaleString()}`;
      crosshairTooltip.hidden = false;
    };
    chart.subscribeCrosshairMove(updateCrosshairTooltip);
    const sessionOverlay = document.createElement("div");
    sessionOverlay.className = "market-chart-session-overlay";
    element.append(sessionOverlay);
    const updateSessionBoundaries = () => {
      const separators = timeline.sessions.flatMap((session, index) => {
        const openX = chart.timeScale().timeToCoordinate(time(session.openTime));
        const closeX = chart.timeScale().timeToCoordinate(time(session.closeTime));
        if (openX === null || closeX === null) return [];
        const boundaries: HTMLSpanElement[] = [];
        if (index === 0) {
          const open = document.createElement("span");
          open.className = "market-chart-session-separator";
          open.style.left = `${String(Math.round(openX))}px`;
          boundaries.push(open);
        }
        const next = timeline.sessions[index + 1];
        const nextOpenX =
          next === undefined ? null : chart.timeScale().timeToCoordinate(time(next.openTime));
        const separator = document.createElement("span");
        separator.className =
          nextOpenX === null
            ? "market-chart-session-separator"
            : "market-chart-session-separator market-chart-session-separator--gap";
        separator.style.left = `${String(Math.round(closeX))}px`;
        separator.style.width = `${String(Math.max(2, (nextOpenX ?? closeX) - closeX))}px`;
        boundaries.push(separator);
        return boundaries;
      });
      sessionOverlay.replaceChildren(...separators);
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(updateSessionBoundaries);
    const resizeObserver = new ResizeObserver(updateSessionBoundaries);
    resizeObserver.observe(element);
    chart.timeScale().fitContent();
    const firstFrame = requestAnimationFrame(updateSessionBoundaries);
    return () => {
      cancelAnimationFrame(firstFrame);
      resizeObserver.disconnect();
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(updateSessionBoundaries);
      chart.unsubscribeCrosshairMove(updateCrosshairTooltip);
      chart.unsubscribeClick(handleClick);
      crosshairTooltip.remove();
      sessionOverlay.remove();
      chartApi.current = null;
      annotationPrimitive.current = null;
      logicalIndices.current = new Map();
      chart.remove();
    };
  }, [
    fills,
    selectedFillId,
    timeline,
    timeframe,
    onAnnotationSelect,
    onBarSelect,
    evidenceSessionDates,
  ]);

  useEffect(() => {
    annotationPrimitive.current?.setAnnotations(annotations, selectedAnnotationId);
  }, [
    annotations,
    selectedAnnotationId,
    fills,
    selectedFillId,
    timeline,
    timeframe,
    onAnnotationSelect,
    onBarSelect,
    evidenceSessionDates,
  ]);

  useEffect(() => {
    if (focusAt === undefined || chartApi.current === null) return;
    const selectedTime = chartTimeForInstant(timeline, focusAt);
    const barIndex =
      selectedTime === undefined ? undefined : logicalIndices.current.get(selectedTime);
    if (barIndex === undefined) return;
    chartApi.current.timeScale().setVisibleLogicalRange({
      from: Math.max(-1, barIndex - 30),
      to: Math.min(logicalIndices.current.size, barIndex + 30),
    });
  }, [fills, focusAt, selectedFillId, timeline]);

  const zoom = (factor: number) => {
    const range = chartApi.current?.timeScale().getVisibleLogicalRange();
    if (range === null || range === undefined) return;
    const center = (range.from + range.to) / 2;
    const half = ((range.to - range.from) / 2) * factor;
    chartApi.current
      ?.timeScale()
      .setVisibleLogicalRange({ from: center - half, to: center + half });
  };

  return (
    <div className="market-chart-shell">
      <div className="market-chart-controls" aria-label="Chart controls">
        <button aria-label="Zoom in" onClick={() => zoom(0.72)} type="button">
          +
        </button>
        <button aria-label="Zoom out" onClick={() => zoom(1.4)} type="button">
          −
        </button>
        <button
          aria-label="Fit chart"
          onClick={() => chartApi.current?.timeScale().fitContent()}
          type="button"
        >
          Fit
        </button>
      </div>
      <div className="market-chart" ref={container} />
    </div>
  );
}
